(() => {
  'use strict';

  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/i;
  const CART_PAGE_RE = /\/cart\/?$/i;
  const COMPACT_CART_QUERY = '(max-width: 1024px), (hover: none), (pointer: coarse)';
  const INTERNAL_HEADER = 'X-KB-Custom-Cart';
  const MANAGED_HEADER = 'X-KB-Custom-Cart-Managed';
  const GIFT_PROPERTY = 'Gift';
  const GIFT_TOKEN_PROPERTY = '_KB_GiftToken';
  const GIFT_WRAP_FOR_PROPERTY = '_KB_GiftWrapFor';
  const GIFT_WRAP_TITLE_PROPERTY = 'Gift wrap for';
  const GIFT_WRAP_VARIANT_PROPERTY = '_KB_GiftWrapParentVariant';
  let goKwikFlowUntil = 0;
  let removalConfirmState = null;

  const getRemovalItemLabel = (value) => {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    return normalized || 'this item';
  };

  const ensureRemovalConfirmation = () => {
    let modal = document.querySelector('[data-kb-removal-confirm]');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'kb-removal-confirm';
    modal.dataset.kbRemovalConfirm = '';
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="kb-removal-confirm__backdrop" data-kb-removal-cancel></div>
      <section class="kb-removal-confirm__dialog" role="alertdialog" aria-modal="true" aria-labelledby="KbRemovalConfirmTitle" aria-describedby="KbRemovalConfirmMessage" tabindex="-1">
        <button type="button" class="kb-removal-confirm__close" data-kb-removal-cancel aria-label="Keep item in cart">×</button>
        <div class="kb-removal-confirm__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M4.5 7h15M9 7V4.8h6V7m-8.5 0 .8 12h9.4l.8-12M10 10.5v5.5M14 10.5v5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
        </div>
        <p class="kb-removal-confirm__eyebrow">Confirm removal</p>
        <h2 class="kb-removal-confirm__title" id="KbRemovalConfirmTitle">Remove this item?</h2>
        <p class="kb-removal-confirm__message" id="KbRemovalConfirmMessage">Are you sure you want to remove this item from your cart?</p>
        <div class="kb-removal-confirm__actions">
          <button type="button" class="kb-removal-confirm__button kb-removal-confirm__button--keep" data-kb-removal-cancel>No, Keep It</button>
          <button type="button" class="kb-removal-confirm__button kb-removal-confirm__button--remove" data-kb-removal-approve>Yes, Remove</button>
        </div>
      </section>`;

    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-kb-removal-approve]')) {
        settleRemovalConfirmation(true);
        return;
      }
      if (event.target.closest('[data-kb-removal-cancel]')) {
        settleRemovalConfirmation(false);
      }
    });

    return modal;
  };

  const settleRemovalConfirmation = (confirmed) => {
    const state = removalConfirmState;
    if (!state) return;

    removalConfirmState = null;
    state.modal.classList.remove('is-visible');
    state.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('kb-removal-confirm-open');

    window.setTimeout(() => {
      if (!removalConfirmState) state.modal.hidden = true;
    }, 220);

    if (state.trigger?.isConnected) {
      window.requestAnimationFrame(() => state.trigger.focus({ preventScroll: true }));
    }
    state.resolve(Boolean(confirmed));
  };

  const confirmCartRemoval = ({ itemTitle, title, message } = {}) => {
    const modal = ensureRemovalConfirmation();
    const itemLabel = getRemovalItemLabel(itemTitle);
    const titleElement = modal.querySelector('.kb-removal-confirm__title');
    const messageElement = modal.querySelector('.kb-removal-confirm__message');
    const keepButton = modal.querySelector('.kb-removal-confirm__button--keep');

    if (removalConfirmState) settleRemovalConfirmation(false);

    titleElement.textContent = title || 'Remove this item?';
    messageElement.textContent = message || `Are you sure you want to remove ${itemLabel} from your cart?`;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('kb-removal-confirm-open');

    return new Promise((resolve) => {
      removalConfirmState = {
        modal,
        resolve,
        trigger: document.activeElement instanceof HTMLElement ? document.activeElement : null,
      };
      window.requestAnimationFrame(() => {
        modal.classList.add('is-visible');
        keepButton?.focus({ preventScroll: true });
      });
    });
  };

  window.KBConfirmCartRemoval = confirmCartRemoval;

  document.addEventListener('keydown', (event) => {
    if (!removalConfirmState) return;

    if (event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      settleRemovalConfirmation(false);
      return;
    }

    if (event.key !== 'Tab') return;
    const dialog = removalConfirmState.modal.querySelector('.kb-removal-confirm__dialog');
    const focusable = Array.from(
      dialog?.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])') || []
    ).filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  const isModifiedClick = (event) =>
    event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

  class KBCustomCart extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.cart = null;
      this.activeElement = null;
      this.refreshTimer = null;
      this.refreshPromise = null;
      this.toastTimer = null;
      this.noticeTimer = null;
      this.offerSlideTimer = null;
      this.offerCopyTimer = null;
      this.offerScrollFrame = null;
      this.offerSlideIndex = 0;
      this.pendingOpen = false;
      this.pendingToast = false;
      this.pendingAnnounce = false;
      this.quantityTimers = new Map();
      this.productCache = new Map();
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundClick = this.onClick.bind(this);
      this.boundCheckoutClick = this.onCheckoutClick.bind(this);
      this.boundCheckoutSubmit = this.onCheckoutSubmit.bind(this);
      this.boundChange = this.onChange.bind(this);
      this.boundViewportChange = this.onViewportChange.bind(this);
      this.boundOffersEditorRefresh = this.onOffersEditorRefresh.bind(this);
      this.boundOfferScroll = this.onOfferScroll.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-kb-cart-config]')?.textContent || '{}');
      } catch (error) {
        console.error('Custom cart configuration could not be read.', error);
      }

      this.availableOffersConfig = this.readAvailableOffersConfig();
      this.panel = this.querySelector('.kb-cart__panel');
      this.itemsElement = this.querySelector('[data-kb-cart-items]');
      this.emptyElement = this.querySelector('[data-kb-cart-empty]');
      this.footerElement = this.querySelector('[data-kb-cart-footer]');
      this.totalElement = this.querySelector('[data-kb-cart-total]');
      this.savingsElement = this.querySelector('[data-kb-cart-savings]');
      this.countElement = this.querySelector('[data-kb-cart-count]');
      this.loadingElement = this.querySelector('[data-kb-cart-loading]');
      this.statusElement = this.querySelector('[data-kb-cart-status]');
      this.toastElement = this.querySelector('[data-kb-cart-toast]');
      this.noticeElement = this.querySelector('[data-kb-cart-notice]');
      this.breakdownElement = this.querySelector('[data-kb-cart-breakdown]');
      this.breakdownCaptionElement = this.querySelector('[data-kb-breakdown-caption]');
      this.breakdownPanelElement = this.querySelector('[data-kb-breakdown-panel]');
      this.breakdownToggleElement = this.querySelector('[data-kb-breakdown-toggle]');
      this.discountCodeCardElement = this.querySelector('[data-kb-cart-discount-code-card]');
      this.discountCodesElement = this.querySelector('[data-kb-cart-discount-codes]');
      this.availableOffersElement = this.querySelector('[data-kb-cart-available-offers]');
      this.availableOffersListElement = this.querySelector('[data-kb-available-offers-list]');
      this.availableOffersHeadingElement = this.querySelector('[data-kb-available-offers-heading]');
      this.availableOffersSubheadingElement = this.querySelector('[data-kb-available-offers-subheading]');
      this.availableOffersCountElement = this.querySelector('[data-kb-available-offers-count]');
      this.availableOffersDotsElement = this.querySelector('[data-kb-available-offers-dots]');
      this.availableOffersNavElement = this.querySelector('[data-kb-available-offers-nav]');
      this.offerPrevElement = this.querySelector('[data-kb-offer-prev]');
      this.offerNextElement = this.querySelector('[data-kb-offer-next]');
      this.offerCopyStatusElement = this.querySelector('[data-kb-offer-copy-status]');

      this.addEventListener('click', this.boundCheckoutClick, true);
      this.addEventListener('submit', this.boundCheckoutSubmit, true);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('change', this.boundChange);
      this.availableOffersListElement?.addEventListener('scroll', this.boundOfferScroll, { passive: true });
      document.addEventListener('keydown', this.boundKeydown);
      window.addEventListener('resize', this.boundViewportChange, { passive: true });
      window.addEventListener('orientationchange', this.boundViewportChange, { passive: true });
      window.visualViewport?.addEventListener('resize', this.boundViewportChange, { passive: true });
      document.addEventListener('shopify:section:load', this.boundOffersEditorRefresh);
      document.addEventListener('shopify:section:reorder', this.boundOffersEditorRefresh);
      document.addEventListener('shopify:block:select', this.boundOffersEditorRefresh);
      document.addEventListener('shopify:block:deselect', this.boundOffersEditorRefresh);
      document.body.classList.add('kb-custom-cart-enabled');

      this.refresh({ open: false, toast: false, announce: false }).catch(() => {});
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundCheckoutClick, true);
      this.removeEventListener('submit', this.boundCheckoutSubmit, true);
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('change', this.boundChange);
      this.availableOffersListElement?.removeEventListener('scroll', this.boundOfferScroll);
      document.removeEventListener('keydown', this.boundKeydown);
      window.removeEventListener('resize', this.boundViewportChange);
      window.removeEventListener('orientationchange', this.boundViewportChange);
      window.visualViewport?.removeEventListener('resize', this.boundViewportChange);
      document.removeEventListener('shopify:section:load', this.boundOffersEditorRefresh);
      document.removeEventListener('shopify:section:reorder', this.boundOffersEditorRefresh);
      document.removeEventListener('shopify:block:select', this.boundOffersEditorRefresh);
      document.removeEventListener('shopify:block:deselect', this.boundOffersEditorRefresh);
      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.noticeTimer);
      window.clearTimeout(this.offerCopyTimer);
      window.clearInterval(this.offerSlideTimer);
      window.cancelAnimationFrame(this.offerScrollFrame);
    }

    readAvailableOffersConfig() {
      const source = document.querySelector('[data-kb-cart-offers-config]');
      if (!source) return { enabled: false, offers: [] };

      try {
        const parsed = JSON.parse(source.textContent || '{}');
        const offers = Array.isArray(parsed.offers)
          ? parsed.offers
              .map((offer) => ({
                id: String(offer?.id || ''),
                code: String(offer?.code || '').trim(),
                title: String(offer?.title || '').trim(),
                description: String(offer?.description || '').trim(),
                badge: String(offer?.badge || '').trim(),
              }))
              .filter((offer) => offer.code)
          : [];

        return {
          enabled: parsed.enabled !== false,
          heading: String(parsed.heading || 'Available offers').trim() || 'Available offers',
          subheading:
            String(parsed.subheading || 'Swipe through offers and copy a code for checkout.').trim() ||
            'Swipe through offers and copy a code for checkout.',
          copyText: String(parsed.copyText || 'Copy code').trim() || 'Copy code',
          copiedText: String(parsed.copiedText || 'Copied!').trim() || 'Copied!',
          autoRotate: parsed.autoRotate !== false,
          rotateInterval: Math.max(3000, Math.min(10000, Number(parsed.rotateInterval || 5000))),
          offers,
        };
      } catch (error) {
        console.error('Cart discount offers configuration could not be read.', error);
        return { enabled: false, offers: [] };
      }
    }

    onOffersEditorRefresh() {
      window.requestAnimationFrame(() => {
        this.availableOffersConfig = this.readAvailableOffersConfig();
        this.renderAvailableOffers(this.cart || { items: [], discount_codes: [] });
      });
    }

    getSectionsToRender() {
      return [{ id: 'cart-icon-bubble' }];
    }

    renderContents() {
      this.vibrateOnSuccessfulAdd();
      this.requestRefresh({ open: true, toast: true, announce: true });
    }

    setActiveElement(element) {
      this.activeElement = element || document.activeElement;
    }

    isCompactViewport() {
      return window.matchMedia(COMPACT_CART_QUERY).matches;
    }

    vibrateOnSuccessfulAdd(sourceForm = null) {
      if (!this.isCompactViewport()) return;
      if (sourceForm?.matches?.('.premium-card__form, .premium-qv-modal__form')) return;
      if (!window.navigator || typeof window.navigator.vibrate !== 'function') return;

      const now = Date.now();
      if (now - (this.lastAddVibrationAt || 0) < 500) return;
      this.lastAddVibrationAt = now;

      try {
        window.navigator.vibrate(45);
      } catch (error) {
        // Vibration support varies by browser and device; cart behavior must remain unaffected.
      }
    }

    onViewportChange() {
      if (this.classList.contains('is-toast-visible')) this.positionToast();
    }

    toggleBreakdown(button) {
      if (!button) return;
      const controls = button.getAttribute('aria-controls');
      const panel = controls
        ? document.getElementById(controls)
        : button.closest('.kb-cart__breakdown, .kb-native-cart-breakdown')?.querySelector('[data-kb-breakdown-panel]');
      if (!panel) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      panel.hidden = expanded;
      button.closest('.kb-cart__breakdown, .kb-native-cart-breakdown')?.classList.toggle('is-open', !expanded);
    }

    requestRefresh({ open = false, toast = false, announce = false } = {}) {
      this.pendingOpen = this.pendingOpen || open;
      this.pendingToast = this.pendingToast || toast;
      this.pendingAnnounce = this.pendingAnnounce || announce;
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => {
        const options = {
          open: this.pendingOpen,
          toast: this.pendingToast,
          announce: this.pendingAnnounce,
        };
        this.pendingOpen = false;
        this.pendingToast = false;
        this.pendingAnnounce = false;
        this.refresh(options).catch(() => {});
      }, 35);
    }

    async refresh({ open = false, toast = false, announce = false } = {}) {
      if (this.refreshPromise) {
        this.pendingOpen = this.pendingOpen || open;
        this.pendingToast = this.pendingToast || toast;
        this.pendingAnnounce = this.pendingAnnounce || announce;
        return this.refreshPromise;
      }

      this.setLoading(true);
      this.refreshPromise = fetch(`${this.config.cartUrl}.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          [INTERNAL_HEADER]: '1',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error(this.config.errorText || 'Unable to load cart.');
          return response.json();
        })
        .then((cart) => this.reconcileGiftWrapCart(cart))
        .then((cart) => this.enrichCart(cart))
        .then((cart) => {
          const shouldOpen = open || this.pendingOpen;
          const shouldToast = toast || this.pendingToast;
          const shouldAnnounce = announce || this.pendingAnnounce;
          this.pendingOpen = false;
          this.pendingToast = false;
          this.pendingAnnounce = false;

          this.cart = cart;
          this.render(cart);
          document.dispatchEvent(new CustomEvent('kb:cart:updated', { detail: { cart } }));

          if (this.isCompactViewport()) {
            if (shouldToast) {
              this.close();
              this.showToast();
            } else if (shouldOpen) {
              this.open();
            }
          } else if (shouldOpen) {
            this.open();
          }

          if (shouldAnnounce) {
            this.announce(`${cart.item_count} item${cart.item_count === 1 ? '' : 's'} in cart.`);
          }
          return cart;
        })
        .catch((error) => {
          console.error(error);
          this.announce(error.message || this.config.errorText || 'Unable to update cart.');
          throw error;
        })
        .finally(() => {
          this.refreshPromise = null;
          this.setLoading(false);
        });

      return this.refreshPromise;
    }

    async enrichCart(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const handles = [...new Set(items.map((item) => item.handle).filter(Boolean))];
      if (!handles.length) return cart;

      await Promise.all(handles.map((handle) => this.getProductData(handle)));

      items.forEach((item) => {
        const product = this.productCache.get(item.handle)?.value;
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const variant = variants.find((entry) => Number(entry.id) === Number(item.variant_id));
        item.kb_compare_at_price = Number(variant?.compare_at_price || 0);
      });

      return cart;
    }

    async getProductData(handle) {
      const existing = this.productCache.get(handle);
      if (existing?.promise) return existing.promise;
      if (existing?.value) return existing.value;

      const root = String(this.config.rootUrl || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const url = `${root}products/${encodeURIComponent(handle)}.js`;
      const record = { value: null, promise: null };
      record.promise = fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          [INTERNAL_HEADER]: '1',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load product pricing.');
          return response.json();
        })
        .then((product) => {
          record.value = product;
          return product;
        })
        .catch(() => null)
        .finally(() => {
          record.promise = null;
        });
      this.productCache.set(handle, record);
      return record.promise;
    }

    open() {
      this.hideToast();
      if (!this.classList.contains('is-active')) {
        this.activeElement = this.activeElement || document.activeElement;
      }
      this.classList.add('is-active');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('kb-cart-open');
      this.startOfferSlideshow();
      window.requestAnimationFrame(() => this.panel?.focus({ preventScroll: true }));
      document.dispatchEvent(new CustomEvent('kb:cart:opened'));
    }

    close({ restoreFocus = true } = {}) {
      const wasActive = this.classList.contains('is-active');
      this.classList.remove('is-active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('kb-cart-open');
      this.stopOfferSlideshow();
      if (restoreFocus && wasActive && this.activeElement && this.activeElement.isConnected) {
        this.activeElement.focus({ preventScroll: true });
      }
      this.activeElement = null;
      if (wasActive) document.dispatchEvent(new CustomEvent('kb:cart:closed'));
    }

    showToast() {
      if (!this.toastElement || !this.isCompactViewport()) return;
      this.close();
      window.clearTimeout(this.toastTimer);
      this.positionToast();
      this.toastElement.textContent = this.config.addedText || 'ITEM ADDED TO CART';
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-toast-visible');
      this.toastElement.classList.add('is-visible');
      this.toastElement.setAttribute('aria-hidden', 'false');
      this.announce(this.config.addedText || 'Item added to cart.');

      window.requestAnimationFrame(() => this.positionToast());
      window.setTimeout(() => this.positionToast(), 140);
      window.setTimeout(() => this.positionToast(), 320);

      this.toastTimer = window.setTimeout(() => this.hideToast(), 2400);
    }

    hideToast() {
      window.clearTimeout(this.toastTimer);
      this.classList.remove('is-toast-visible');
      this.toastElement?.classList.remove('is-visible');
      this.toastElement?.setAttribute('aria-hidden', 'true');
      if (!this.classList.contains('is-active')) this.setAttribute('aria-hidden', 'true');
    }

    showNotice(message, tone = 'success') {
      if (!this.noticeElement || !message) return;
      window.clearTimeout(this.noticeTimer);
      this.noticeElement.textContent = message;
      this.noticeElement.dataset.tone = tone;
      this.noticeElement.hidden = false;
      this.noticeElement.classList.add('is-visible');
      this.noticeTimer = window.setTimeout(() => {
        this.noticeElement.classList.remove('is-visible');
        window.setTimeout(() => {
          if (!this.noticeElement.classList.contains('is-visible')) this.noticeElement.hidden = true;
        }, 190);
      }, tone === 'error' ? 5200 : 3200);
    }

    positionToast() {
      if (!this.toastElement || !this.isCompactViewport()) return;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      let clearance = 18;

      document.querySelectorAll('[data-mobile-sticky-bar]').forEach((bar) => {
        const style = window.getComputedStyle(bar);
        const rect = bar.getBoundingClientRect();
        const visible =
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          Number.parseFloat(style.opacity || '1') > 0.05 &&
          rect.width > 0 &&
          rect.height > 0 &&
          rect.top < viewportHeight &&
          rect.bottom > 0;

        if (visible) clearance = Math.max(clearance, viewportHeight - rect.top + 12);
      });

      this.style.setProperty('--kb-toast-bottom', `${Math.round(clearance)}px`);
    }

    onKeydown(event) {
      if (removalConfirmState) return;
      if (!this.classList.contains('is-active')) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.panel) return;

      const focusable = Array.from(
        this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onCheckoutClick(event) {
      const checkoutControl = event.target?.closest?.(
        '.kb-cart__checkout, .gokwik-checkout button, [name="checkout"], [onclick*="onCheckoutClick"]'
      );
      if (!checkoutControl || !this.contains(checkoutControl)) return;

      // Close before target/inline checkout handlers execute. Do not prevent the
      // click: Shopify and GoKwik must receive the original user gesture.
      this.hideToast();
      this.close({ restoreFocus: false });
    }

    onCheckoutSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('.kb-cart__native-checkout-form') && !form.querySelector('[name="checkout"]')) return;
      this.hideToast();
      this.close({ restoreFocus: false });
    }

    async onClick(event) {
      const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const clickedOutsidePanel =
        this.classList.contains('is-active') &&
        this.panel &&
        !eventPath.includes(this.panel);

      if (clickedOutsidePanel) {
        event.preventDefault();
        this.close();
        return;
      }

      const copyDiscountButton = event.target.closest('[data-kb-copy-discount]');
      if (copyDiscountButton) {
        event.preventDefault();
        await this.copyDiscountCode(copyDiscountButton.dataset.kbCopyDiscount, copyDiscountButton);
        return;
      }

      const offerPrevious = event.target.closest('[data-kb-offer-prev]');
      if (offerPrevious) {
        event.preventDefault();
        this.goToOfferSlide(this.offerSlideIndex - 1, { restart: true });
        return;
      }

      const offerNext = event.target.closest('[data-kb-offer-next]');
      if (offerNext) {
        event.preventDefault();
        this.goToOfferSlide(this.offerSlideIndex + 1, { restart: true });
        return;
      }

      const offerDot = event.target.closest('[data-kb-offer-dot]');
      if (offerDot) {
        event.preventDefault();
        this.goToOfferSlide(Number(offerDot.dataset.kbOfferDot || 0), { restart: true });
        return;
      }

      const breakdownToggle = event.target.closest('[data-kb-breakdown-toggle]');
      if (breakdownToggle) {
        event.preventDefault();
        this.toggleBreakdown(breakdownToggle);
        return;
      }

      const closeControl = event.target.closest('[data-kb-cart-close]');
      if (closeControl) {
        event.preventDefault();
        this.close();
        return;
      }

      const removeGiftWrap = event.target.closest('[data-kb-remove-gift-token]');
      if (removeGiftWrap) {
        event.preventDefault();
        const confirmed = await confirmCartRemoval({
          itemTitle: 'gift wrapping',
          title: 'Remove gift wrapping?',
          message: 'Are you sure you want to remove the selected gift wrapping from this item?',
        });
        if (confirmed) {
          this.removeGiftByToken(removeGiftWrap.dataset.kbRemoveGiftToken, removeGiftWrap);
        }
        return;
      }

      const quantityButton = event.target.closest('[data-kb-quantity-action]');
      if (quantityButton) {
        event.preventDefault();
        const line = Number(quantityButton.dataset.line);
        const current = Number(quantityButton.dataset.quantity);
        const delta = quantityButton.dataset.kbQuantityAction === 'increase' ? 1 : -1;
        if (delta < 0 && current <= 1) {
          const item = this.cart?.items?.[line - 1];
          const confirmed = await confirmCartRemoval({
            itemTitle: item?.product_title || item?.title,
          });
          if (!confirmed) return;
        }
        this.changeLine(line, Math.max(0, current + delta));
        return;
      }

      const removeButton = event.target.closest('[data-kb-remove-line]');
      if (removeButton) {
        event.preventDefault();
        const line = Number(removeButton.dataset.kbRemoveLine);
        const item = this.cart?.items?.[line - 1];
        const confirmed = await confirmCartRemoval({
          itemTitle: item?.product_title || item?.title,
        });
        if (confirmed) this.changeLine(line, 0);
      }
    }

    async onChange(event) {
      const giftQuantity = event.target.closest('[data-kb-gift-quantity]');
      if (giftQuantity) {
        const quantity = Math.max(0, Number.parseInt(giftQuantity.value, 10) || 0);
        const parentItem = this.cart?.items?.find((item) => item.key === giftQuantity.dataset.key);
        const currentGiftQuantity = parentItem
          ? this.getGiftWrapItemsForItem(parentItem).reduce(
              (sum, item) => sum + Math.max(0, Number(item.quantity || 0)),
              0
            )
          : 0;

        if (quantity === 0 && currentGiftQuantity > 0) {
          const confirmed = await confirmCartRemoval({
            itemTitle: 'gift wrapping',
            title: 'Remove gift wrapping?',
            message: 'Are you sure you want to remove gift wrapping from this item?',
          });
          if (!confirmed) {
            giftQuantity.value = String(currentGiftQuantity);
            return;
          }
        }

        this.updateGiftQuantity(giftQuantity.dataset.key, quantity, giftQuantity);
        return;
      }

      const input = event.target.closest('[data-kb-quantity-input]');
      if (!input) return;
      const line = Number(input.dataset.line);
      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      window.clearTimeout(this.quantityTimers.get(line));

      if (quantity === 0) {
        const item = this.cart?.items?.[line - 1];
        const previousQuantity = Math.max(1, Number(item?.quantity || 1));
        this.quantityTimers.set(line, window.setTimeout(async () => {
          const confirmed = await confirmCartRemoval({
            itemTitle: item?.product_title || item?.title,
          });
          if (confirmed) this.changeLine(line, 0);
          else input.value = String(previousQuantity);
        }, 180));
        return;
      }

      this.quantityTimers.set(line, window.setTimeout(() => this.changeLine(line, quantity), 180));
    }

    isGiftWrapItem(item) {
      const configuredHandle = String(this.config.giftWrapHandle || 'giftwrap').toLowerCase();
      const itemHandle = String(item?.handle || '').toLowerCase();
      return (
        Boolean(item?.properties?.[GIFT_WRAP_FOR_PROPERTY]) ||
        itemHandle === configuredHandle ||
        itemHandle === 'giftwrap' ||
        itemHandle === 'gift-wrap'
      );
    }

    getGiftToken(item) {
      return String(item?.properties?.[GIFT_TOKEN_PROPERTY] || '');
    }

    getGiftLinkTokens(item) {
      const tokens = new Set();
      if (item?.key) tokens.add(String(item.key));
      const legacyToken = this.getGiftToken(item);
      if (legacyToken) tokens.add(legacyToken);
      return tokens;
    }

    getGiftWrapItems(token, cart = this.cart) {
      if (!token || !Array.isArray(cart?.items)) return [];
      return cart.items.filter(
        (entry) => String(entry.properties?.[GIFT_WRAP_FOR_PROPERTY] || '') === String(token)
      );
    }

    getGiftWrapItemsForItem(item, cart = this.cart) {
      if (!item || !Array.isArray(cart?.items)) return [];
      const tokens = this.getGiftLinkTokens(item);
      const directMatches = cart.items.filter(
        (entry) =>
          this.isGiftWrapItem(entry) &&
          tokens.has(String(entry.properties?.[GIFT_WRAP_FOR_PROPERTY] || ''))
      );
      if (directMatches.length) return directMatches;

      const sameVariantParents = cart.items.filter(
        (entry) => !this.isGiftWrapItem(entry) && Number(entry.variant_id) === Number(item.variant_id)
      );
      if (sameVariantParents.length !== 1) return [];
      return cart.items.filter(
        (entry) =>
          this.isGiftWrapItem(entry) &&
          Number(entry.properties?.[GIFT_WRAP_VARIANT_PROPERTY] || 0) === Number(item.variant_id)
      );
    }

    findParentForGiftWrap(giftWrap, cart = this.cart) {
      if (!giftWrap || !Array.isArray(cart?.items)) return null;
      const token = String(giftWrap.properties?.[GIFT_WRAP_FOR_PROPERTY] || '');
      if (token) {
        const exact = cart.items.find(
          (entry) =>
            !this.isGiftWrapItem(entry) &&
            (String(entry.key || '') === token || this.getGiftToken(entry) === token)
        );
        if (exact) return exact;
      }

      const parentVariant = Number(giftWrap.properties?.[GIFT_WRAP_VARIANT_PROPERTY] || 0);
      if (!parentVariant) return null;
      const candidates = cart.items.filter(
        (entry) => !this.isGiftWrapItem(entry) && Number(entry.variant_id) === parentVariant
      );
      return candidates.length === 1 ? candidates[0] : null;
    }

    getGiftWrapQuantityForItem(item, cart = this.cart) {
      return this.getGiftWrapItemsForItem(item, cart).reduce(
        (sum, giftWrap) => sum + Math.max(0, Number(giftWrap.quantity || 0)),
        0
      );
    }

    isGiftSelected(item, cart = this.cart) {
      return this.getGiftWrapQuantityForItem(item, cart) > 0;
    }

    async requestCartChange(payload) {
      const response = await fetch(this.config.cartChangeUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          [INTERNAL_HEADER]: '1',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.config.errorText || 'Unable to update cart.');
      }
      return result;
    }

    async requestCartUpdate(updates) {
      const response = await fetch(this.config.cartUpdateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          [INTERNAL_HEADER]: '1',
        },
        body: JSON.stringify({ updates }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.config.errorText || 'Unable to update cart.');
      }
      return result;
    }

    async reconcileGiftWrapCart(cart) {
      if (!Array.isArray(cart?.items) || !cart.items.length) return cart;
      const linkedGiftWraps = cart.items.filter(
        (item) => this.isGiftWrapItem(item) && item.properties?.[GIFT_WRAP_FOR_PROPERTY]
      );
      if (!linkedGiftWraps.length) return cart;

      const groups = new Map();
      linkedGiftWraps.forEach((giftWrap) => {
        const link = String(giftWrap.properties?.[GIFT_WRAP_FOR_PROPERTY] || '');
        if (!groups.has(link)) groups.set(link, []);
        groups.get(link).push(giftWrap);
      });

      const updates = {};
      groups.forEach((giftWraps) => {
        const parent = this.findParentForGiftWrap(giftWraps[0], cart);
        if (!parent) {
          giftWraps.forEach((giftWrap) => {
            updates[giftWrap.key] = 0;
          });
          return;
        }

        const requestedQuantity = giftWraps.reduce(
          (sum, giftWrap) => sum + Math.max(0, Number(giftWrap.quantity || 0)),
          0
        );
        const desiredQuantity = Math.min(
          Math.max(0, Number(parent.quantity || 0)),
          requestedQuantity
        );

        giftWraps.forEach((giftWrap, index) => {
          const nextQuantity = index === 0 ? desiredQuantity : 0;
          if (Number(giftWrap.quantity || 0) !== nextQuantity) updates[giftWrap.key] = nextQuantity;
        });
      });

      if (!Object.keys(updates).length) return cart;
      return this.requestCartUpdate(updates);
    }

    async changeLine(line, quantity) {
      if (!line || line < 1) return;
      const item = this.cart?.items?.[line - 1];
      if (!item) return;
      this.hideToast();
      this.setLoading(true);

      try {
        const linkedGiftWraps = this.isGiftWrapItem(item) ? [] : this.getGiftWrapItemsForItem(item);
        let cart;

        if (linkedGiftWraps.length) {
          const updates = { [item.key]: quantity };
          const currentGiftQuantity = linkedGiftWraps.reduce(
            (sum, giftWrap) => sum + Math.max(0, Number(giftWrap.quantity || 0)),
            0
          );
          const desiredGiftQuantity = quantity > 0 ? Math.min(currentGiftQuantity, quantity) : 0;
          linkedGiftWraps.forEach((giftWrap, index) => {
            updates[giftWrap.key] = index === 0 ? desiredGiftQuantity : 0;
          });
          cart = await this.requestCartUpdate(updates);
        } else {
          cart = await this.requestCartChange({ id: item.key, quantity });
        }

        cart = await this.reconcileGiftWrapCart(cart);
        cart = await this.enrichCart(cart);
        this.cart = cart;
        this.render(cart);
        this.announce(quantity === 0 ? 'Item removed from cart.' : 'Cart quantity updated.');
        this.open();
      } catch (error) {
        console.error(error);
        this.announce(error.message || this.config.errorText || 'Unable to update cart.');
        this.showNotice(error.message || 'Unable to update the cart.', 'error');
        await this.refresh({ open: true, toast: false, announce: false }).catch(() => {});
      } finally {
        this.setLoading(false);
      }
    }

    async getGiftWrapVariant() {
      const configuredVariantId = Number(this.config.giftWrapVariantId || 0);
      if (configuredVariantId && this.config.giftWrapAvailable !== false) {
        return { id: configuredVariantId, available: true };
      }

      const handles = [...new Set([
        String(this.config.giftWrapHandle || '').trim(),
        'giftwrap',
        'gift-wrap',
      ].filter(Boolean))];

      for (const handle of handles) {
        const product = await this.getProductData(handle);
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const variant = variants.find((entry) => entry.available !== false);
        if (variant?.id) {
          this.config.giftWrapHandle = product.handle || handle;
          this.config.giftWrapVariantId = Number(variant.id);
          this.config.giftWrapAvailable = true;
          return variant;
        }
      }

      const root = String(this.config.rootUrl || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const searchUrl = new URL(`${root}search/suggest.json`, window.location.origin);
      searchUrl.searchParams.set('q', 'giftwrap');
      searchUrl.searchParams.set('resources[type]', 'product');
      searchUrl.searchParams.set('resources[limit]', '10');
      searchUrl.searchParams.set('resources[options][unavailable_products]', 'last');

      try {
        const response = await fetch(searchUrl.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json', [INTERNAL_HEADER]: '1' },
        });
        if (response.ok) {
          const payload = await response.json();
          const products = payload?.resources?.results?.products || [];
          const normalized = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
          const candidate =
            products.find((product) => normalized(product.title) === 'giftwrap' || normalized(product.handle) === 'giftwrap') ||
            products.find((product) => normalized(product.title).includes('giftwrap'));

          if (candidate) {
            let handle = candidate.handle;
            if (!handle && candidate.url) {
              const path = new URL(candidate.url, window.location.origin).pathname;
              handle = path.split('/products/')[1]?.split(/[/?#]/)[0];
            }
            if (handle) {
              const product = await this.getProductData(handle);
              const variant = product?.variants?.find((entry) => entry.available !== false);
              if (variant?.id) {
                this.config.giftWrapHandle = product.handle || handle;
                this.config.giftWrapVariantId = Number(variant.id);
                this.config.giftWrapAvailable = true;
                return variant;
              }
            }
          }
        }
      } catch (error) {
        // The explicit product endpoints above remain the primary resolution path.
      }

      throw new Error('Giftwrap is unavailable. Publish the product to Online Store and keep at least one variant in stock.');
    }

    async ensureGiftToken(item) {
      const existingToken = this.getGiftToken(item);
      if (existingToken) return item;

      const token = `kb-gift-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
      const properties = { ...(item.properties || {}), [GIFT_TOKEN_PROPERTY]: token };
      const cart = await this.requestCartChange({
        id: item.key,
        quantity: Math.max(1, Number(item.quantity || 1)),
        properties,
      });
      const updatedItem = cart?.items?.find(
        (entry) => !this.isGiftWrapItem(entry) && this.getGiftToken(entry) === token
      );
      if (!updatedItem) throw new Error('Unable to link Giftwrap to this cart item.');
      return updatedItem;
    }

    async addGiftWrapProduct(parentItem, giftQuantity) {
      const variant = await this.getGiftWrapVariant();
      const response = await fetch(this.config.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          [INTERNAL_HEADER]: '1',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variant.id),
              quantity: Math.max(
                1,
                Math.min(Number(parentItem.quantity || 1), Number(giftQuantity || 1))
              ),
              properties: {
                [GIFT_WRAP_FOR_PROPERTY]: this.getGiftToken(parentItem) || String(parentItem.key || ''),
                [GIFT_WRAP_VARIANT_PROPERTY]: String(parentItem.variant_id || ''),
                [GIFT_WRAP_TITLE_PROPERTY]: parentItem.product_title || parentItem.title || 'Cart item',
              },
            },
          ],
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add the Giftwrap product.');
      }
      return result;
    }

    async removeGiftByToken(token, control = null) {
      if (!token) return;
      const giftWrap = this.getGiftWrapItems(token)[0];
      const parent = giftWrap ? this.findParentForGiftWrap(giftWrap) : null;
      if (parent) {
        await this.updateGiftQuantity(parent.key, 0, control);
        return;
      }

      const updates = {};
      this.getGiftWrapItems(token).forEach((entry) => {
        updates[entry.key] = 0;
      });
      if (Object.keys(updates).length) await this.requestCartUpdate(updates);
      await this.refresh({ open: true, toast: false, announce: false }).catch(() => {});
      this.showNotice('Gift wrap removed.', 'success');
    }

    async updateGiftQuantity(itemKey, requestedQuantity, control = null) {
      const item = this.cart?.items?.find((entry) => entry.key === itemKey);
      if (!item || this.isGiftWrapItem(item)) return;

      const previousQuantity = this.getGiftWrapQuantityForItem(item);
      const giftQuantity = Math.max(
        0,
        Math.min(Number(item.quantity || 0), Number(requestedQuantity || 0))
      );

      if (control) control.disabled = true;
      this.hideToast();
      this.setLoading(true);

      try {
        const linkedGiftWraps = this.getGiftWrapItemsForItem(item);
        if (giftQuantity > 0 && !linkedGiftWraps.length) {
          const linkedParent = await this.ensureGiftToken(item);
          await this.addGiftWrapProduct(linkedParent, giftQuantity);
        } else if (linkedGiftWraps.length) {
          const updates = {};
          linkedGiftWraps.forEach((giftWrap, index) => {
            updates[giftWrap.key] = index === 0 ? giftQuantity : 0;
          });
          await this.requestCartUpdate(updates);
        }

        await this.refresh({ open: true, toast: false, announce: false });
        const message = giftQuantity > 0
          ? `${giftQuantity} of ${item.quantity} item${Number(item.quantity) === 1 ? '' : 's'} selected for gift wrapping.`
          : 'Gift wrap removed from this item.';
        this.announce(message);
        this.showNotice(message, 'success');
      } catch (error) {
        console.error(error);
        if (control && 'value' in control) control.value = String(previousQuantity);
        const message = error.message || this.config.errorText || 'Unable to update gift wrap.';
        this.announce(message);
        this.showNotice(message, 'error');
        await this.refresh({ open: true, toast: false, announce: false }).catch(() => {});
      } finally {
        if (control) control.disabled = false;
        this.setLoading(false);
      }
    }

    getItemDiscounts(item) {
      const discounts = [];
      const allocations = Array.isArray(item?.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : [];

      allocations.forEach((allocation) => {
        const amount = Math.max(0, Number(allocation?.amount || 0));
        if (!amount) return;
        discounts.push({
          title: allocation?.discount_application?.title || allocation?.title || 'Automatic discount',
          amount,
        });
      });

      if (!discounts.length && Array.isArray(item?.discounts)) {
        item.discounts.forEach((discount) => {
          const amount = Math.max(0, Number(discount?.amount || 0));
          if (amount) discounts.push({ title: discount?.title || 'Automatic discount', amount });
        });
      }

      const originalLine = Number(item?.original_line_price ?? item?.line_price ?? 0);
      const finalLine = Number(item?.final_line_price ?? item?.line_price ?? 0);
      const accounted = discounts.reduce((sum, discount) => sum + discount.amount, 0);
      const remainder = Math.max(0, originalLine - finalLine - accounted);
      if (remainder) discounts.push({ title: 'Additional cart discount', amount: remainder });
      return discounts;
    }

    calculateCartTotals(cart) {
      const totals = {
        mrpTotal: 0,
        sellingSubtotal: 0,
        finalItemsSubtotal: 0,
        productSavings: 0,
        itemDiscounts: 0,
        cartDiscounts: 0,
        totalSavings: 0,
        giftWrapTotal: 0,
        finalTotal: Math.max(0, Number(cart?.total_price || 0)),
      };

      (cart?.items || []).forEach((item) => {
        const quantity = Math.max(0, Number(item.quantity || 0));
        const originalLine = Math.max(0, Number(item.original_line_price ?? item.line_price ?? 0));
        const finalLine = Math.max(0, Number(item.final_line_price ?? item.line_price ?? originalLine));
        const compareAtLine = Math.max(0, Number(item.kb_compare_at_price || 0) * quantity);
        const mrpLine = Math.max(originalLine, compareAtLine);

        totals.mrpTotal += mrpLine;
        totals.sellingSubtotal += originalLine;
        totals.finalItemsSubtotal += finalLine;
        totals.productSavings += Math.max(0, mrpLine - originalLine);
        totals.itemDiscounts += Math.max(0, originalLine - finalLine);
        if (this.isGiftWrapItem(item)) totals.giftWrapTotal += finalLine;
      });

      totals.cartDiscounts = Math.max(0, totals.finalItemsSubtotal - totals.finalTotal);
      totals.totalSavings = Math.max(0, totals.mrpTotal - totals.finalTotal);
      totals.discountPercent = totals.mrpTotal > 0
        ? Math.max(0, Math.round((totals.totalSavings / totals.mrpTotal) * 100))
        : 0;
      return totals;
    }

    collectDiscountCodes(cart) {
      const codes = new Map();
      const add = (code, { applicable = true, amount = 0, sum = false } = {}) => {
        const cleanCode = String(code || '').trim();
        if (!cleanCode) return;
        const key = cleanCode.toLowerCase();
        const existing = codes.get(key) || {
          code: cleanCode,
          applicable: true,
          amount: 0,
        };
        existing.applicable = existing.applicable && applicable !== false;
        existing.amount = sum
          ? existing.amount + Math.max(0, Number(amount || 0))
          : Math.max(existing.amount, Math.max(0, Number(amount || 0)));
        codes.set(key, existing);
      };

      (cart?.discount_codes || []).forEach((discount) => {
        add(discount?.code, { applicable: discount?.applicable !== false });
      });

      (cart?.cart_level_discount_applications || []).forEach((application) => {
        if (String(application?.type || '').toLowerCase() !== 'discount_code') return;
        add(application.title, {
          applicable: true,
          amount: application.total_allocated_amount,
        });
      });

      (cart?.items || []).forEach((item) => {
        (item?.line_level_discount_allocations || []).forEach((allocation) => {
          const application = allocation?.discount_application || {};
          if (String(application.type || '').toLowerCase() !== 'discount_code') return;
          add(application.title, {
            applicable: true,
            amount: allocation.amount,
            sum: true,
          });
        });
      });

      return [...codes.values()];
    }

    getOfferSlides() {
      return this.availableOffersListElement
        ? Array.from(this.availableOffersListElement.querySelectorAll('.kb-cart__available-offer'))
        : [];
    }

    renderAvailableOffers(cart) {
      if (!this.availableOffersElement || !this.availableOffersListElement) return;
      const config = this.availableOffersConfig || { enabled: false, offers: [] };
      const offers = config.enabled && Array.isArray(config.offers) ? config.offers : [];
      const hasCartItems = Number(cart?.item_count || 0) > 0 || (Array.isArray(cart?.items) && cart.items.length > 0);
      this.availableOffersElement.hidden = offers.length === 0 || !hasCartItems;
      this.stopOfferSlideshow();

      if (!offers.length || !hasCartItems) {
        this.availableOffersListElement.replaceChildren();
        this.availableOffersDotsElement?.replaceChildren();
        return;
      }

      if (this.availableOffersHeadingElement) {
        this.availableOffersHeadingElement.textContent = config.heading || 'Available offers';
      }
      if (this.availableOffersSubheadingElement) {
        this.availableOffersSubheadingElement.textContent = config.subheading || '';
      }

      const appliedCodes = new Map(
        this.collectDiscountCodes(cart).map((discount) => [String(discount.code || '').toLowerCase(), discount])
      );
      const fragment = document.createDocumentFragment();

      offers.forEach((offer, index) => {
        const discount = appliedCodes.get(offer.code.toLowerCase());
        const isApplied = Boolean(discount && discount.applicable !== false);
        const card = document.createElement('article');
        card.className = `kb-cart__available-offer${isApplied ? ' is-applied' : ''}`;
        card.setAttribute('role', 'listitem');
        card.setAttribute('aria-label', `Offer ${index + 1} of ${offers.length}: ${offer.code}`);

        const top = document.createElement('div');
        top.className = 'kb-cart__available-offer-top';
        const label = document.createElement('span');
        label.className = 'kb-cart__available-offer-label';
        label.textContent = isApplied ? 'Applied to cart' : 'Exclusive offer';
        top.appendChild(label);
        if (offer.badge) {
          const badge = document.createElement('span');
          badge.className = 'kb-cart__available-offer-badge';
          badge.textContent = offer.badge;
          top.appendChild(badge);
        }

        const body = document.createElement('div');
        body.className = 'kb-cart__available-offer-body';
        const copy = document.createElement('div');
        copy.className = 'kb-cart__available-offer-copy';
        const title = document.createElement('b');
        title.textContent = offer.title || offer.code;
        copy.appendChild(title);
        if (offer.description) {
          const description = document.createElement('small');
          description.textContent = offer.description;
          copy.appendChild(description);
        }

        const codeRow = document.createElement('div');
        codeRow.className = 'kb-cart__available-offer-code-row';
        const code = document.createElement('strong');
        code.className = 'kb-cart__available-offer-code';
        code.textContent = offer.code;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'kb-cart__available-offer-copy-button';
        button.textContent = config.copyText || 'Copy code';
        button.dataset.kbCopyDiscount = offer.code;
        button.setAttribute('aria-label', `Copy discount code ${offer.code}`);
        codeRow.append(code, button);

        card.append(top, body, codeRow);
        fragment.appendChild(card);
      });

      this.availableOffersListElement.replaceChildren(fragment);
      this.offerSlideIndex = 0;
      this.renderOfferDots(offers.length);
      this.updateOfferSliderControls();
      window.requestAnimationFrame(() => {
        this.availableOffersListElement.scrollLeft = 0;
        if (this.classList.contains('is-active')) this.startOfferSlideshow();
      });
    }

    renderOfferDots(count) {
      if (!this.availableOffersDotsElement) return;
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.dataset.kbOfferDot = String(index);
        dot.setAttribute('aria-label', `Show discount offer ${index + 1}`);
        fragment.appendChild(dot);
      }
      this.availableOffersDotsElement.replaceChildren(fragment);
      this.availableOffersDotsElement.hidden = count <= 1;
      if (this.availableOffersNavElement) this.availableOffersNavElement.hidden = count <= 1;
    }

    updateOfferSliderControls() {
      const slides = this.getOfferSlides();
      if (!slides.length) return;
      const index = Math.max(0, Math.min(this.offerSlideIndex, slides.length - 1));
      this.offerSlideIndex = index;
      this.availableOffersDotsElement?.querySelectorAll('[data-kb-offer-dot]').forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (this.availableOffersCountElement) {
        this.availableOffersCountElement.textContent = `${index + 1} / ${slides.length}`;
        this.availableOffersCountElement.setAttribute('aria-label', `Offer ${index + 1} of ${slides.length}`);
      }
    }

    goToOfferSlide(index, { smooth = true, restart = false } = {}) {
      const slides = this.getOfferSlides();
      if (!slides.length || !this.availableOffersListElement) return;
      const normalized = ((Number(index) % slides.length) + slides.length) % slides.length;
      this.offerSlideIndex = normalized;
      const firstOffset = slides[0]?.offsetLeft || 0;
      const left = Math.max(0, slides[normalized].offsetLeft - firstOffset);
      const behavior = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';
      if (typeof this.availableOffersListElement.scrollTo === 'function') {
        this.availableOffersListElement.scrollTo({ left, behavior });
      } else {
        this.availableOffersListElement.scrollLeft = left;
      }
      this.updateOfferSliderControls();
      if (restart) this.startOfferSlideshow();
    }

    onOfferScroll() {
      window.cancelAnimationFrame(this.offerScrollFrame);
      this.offerScrollFrame = window.requestAnimationFrame(() => {
        const slides = this.getOfferSlides();
        if (!slides.length || !this.availableOffersListElement) return;
        const firstOffset = slides[0]?.offsetLeft || 0;
        const current = this.availableOffersListElement.scrollLeft;
        let nearest = 0;
        let distance = Number.POSITIVE_INFINITY;
        slides.forEach((slide, index) => {
          const nextDistance = Math.abs(slide.offsetLeft - firstOffset - current);
          if (nextDistance < distance) {
            nearest = index;
            distance = nextDistance;
          }
        });
        if (nearest !== this.offerSlideIndex) {
          this.offerSlideIndex = nearest;
          this.updateOfferSliderControls();
        }
      });
    }

    startOfferSlideshow() {
      this.stopOfferSlideshow();
      const slides = this.getOfferSlides();
      const config = this.availableOffersConfig || {};
      if (!config.autoRotate || slides.length <= 1 || !this.classList.contains('is-active')) return;
      const interval = Math.max(3000, Math.min(10000, Number(config.rotateInterval || 5000)));
      this.offerSlideTimer = window.setInterval(() => {
        this.goToOfferSlide(this.offerSlideIndex + 1, { smooth: true });
      }, interval);
    }

    stopOfferSlideshow() {
      window.clearInterval(this.offerSlideTimer);
      this.offerSlideTimer = null;
    }

    async copyDiscountCode(code, button = null) {
      const cleanCode = String(code || '').trim();
      if (!cleanCode) return;
      let copied = false;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(cleanCode);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }

      if (!copied) {
        const field = document.createElement('textarea');
        field.value = cleanCode;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        try {
          copied = document.execCommand('copy');
        } catch (error) {
          copied = false;
        }
        field.remove();
      }

      const config = this.availableOffersConfig || {};
      const originalText = config.copyText || 'Copy code';
      const message = copied ? `${cleanCode} copied to clipboard.` : `Select and copy code ${cleanCode}.`;
      if (this.offerCopyStatusElement) this.offerCopyStatusElement.textContent = message;
      this.announce(message);

      if (button) {
        window.clearTimeout(this.offerCopyTimer);
        button.textContent = copied ? config.copiedText || 'Copied!' : cleanCode;
        button.classList.toggle('is-copied', copied);
        this.offerCopyTimer = window.setTimeout(() => {
          if (!button.isConnected) return;
          button.textContent = originalText;
          button.classList.remove('is-copied');
        }, 1800);
      }
      this.startOfferSlideshow();
    }

    collectDiscountApplications(cart) {
      const aggregated = new Map();
      const add = (title, amount, scope, allowZero = false) => {
        const cleanTitle = String(title || 'Automatic discount').trim() || 'Automatic discount';
        const value = Math.max(0, Number(amount || 0));
        if (!value && !allowZero) return;
        const key = `${scope}:${cleanTitle.toLowerCase()}`;
        const existing = aggregated.get(key) || { title: cleanTitle, amount: 0, scope };
        existing.amount += value;
        aggregated.set(key, existing);
      };

      (cart?.items || []).forEach((item) => {
        const allocations = Array.isArray(item?.line_level_discount_allocations)
          ? item.line_level_discount_allocations
          : [];
        if (allocations.length) {
          allocations.forEach((allocation) => {
            const application = allocation?.discount_application || {};
            const scope = String(application.type || '').toLowerCase() === 'discount_code'
              ? 'Discount code'
              : 'Item offer';
            add(application.title || allocation.title || 'Automatic discount', allocation.amount, scope);
          });
        } else {
          this.getItemDiscounts(item).forEach((discount) => add(discount.title, discount.amount, 'Item offer'));
        }
      });

      let allocatedCartDiscount = 0;
      (cart?.cart_level_discount_applications || []).forEach((discount) => {
        const amount = Math.max(0, Number(discount.total_allocated_amount || 0));
        allocatedCartDiscount += amount;
        const scope = String(discount.type || '').toLowerCase() === 'discount_code'
          ? 'Discount code'
          : 'Cart offer';
        add(discount.title || 'Cart discount', amount, scope);
      });

      const totals = this.calculateCartTotals(cart);
      const unlabelledCartDiscount = Math.max(0, totals.cartDiscounts - allocatedCartDiscount);
      if (unlabelledCartDiscount) add('Additional cart discount', unlabelledCartDiscount, 'Cart offer');

      this.collectDiscountCodes(cart).forEach((discount) => {
        const alreadyListed = [...aggregated.values()].some(
          (entry) => entry.title.toLowerCase() === discount.code.toLowerCase()
        );
        if (!alreadyListed) add(discount.code, discount.amount, 'Discount code', true);
      });
      return [...aggregated.values()];
    }

    createDiscountCodeCard(codes, native = false) {
      if (!codes.length) return null;
      const card = document.createElement('div');
      card.className = `kb-cart__discount-code-card${native ? ' kb-native-discount-code-card' : ''}`;

      const heading = document.createElement('div');
      heading.className = 'kb-cart__discount-code-heading';
      const icon = document.createElement('span');
      icon.className = 'kb-cart__discount-code-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '%';
      const copy = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = 'Shopify discount codes';
      const hint = document.createElement('small');
      hint.textContent = `${codes.length} code${codes.length === 1 ? '' : 's'} attached to this cart`;
      copy.append(title, hint);
      heading.append(icon, copy);

      const list = document.createElement('div');
      list.className = 'kb-cart__discount-code-list';
      this.renderDiscountCodeChips(list, codes);
      card.append(heading, list);
      return card;
    }

    renderDiscountCodeChips(container, codes) {
      if (!container) return;
      const fragment = document.createDocumentFragment();
      codes.forEach((discount) => {
        const chip = document.createElement('span');
        chip.className = `kb-cart__discount-code${discount.applicable === false ? ' is-unavailable' : ''}`;
        const code = document.createElement('strong');
        code.textContent = discount.code;
        const status = document.createElement('small');
        status.textContent = discount.applicable === false
          ? 'Not applicable'
          : discount.amount > 0
            ? `Saved ${this.formatMoney(discount.amount)}`
            : 'Applied';
        chip.append(code, status);
        fragment.appendChild(chip);
      });
      container.replaceChildren(fragment);
    }

    createBreakdownRow(label, amount, modifier = '', prefix = '') {

      const row = document.createElement('div');
      row.className = `kb-cart__breakdown-row${modifier ? ` kb-cart__breakdown-row--${modifier}` : ''}`;
      const name = document.createElement('span');
      name.textContent = label;
      const value = document.createElement('strong');
      value.textContent = `${prefix}${this.formatMoney(amount)}`;
      row.append(name, value);
      return row;
    }

    renderCartBreakdown(cart, totals) {
      this.totalElement.textContent = this.formatMoney(totals.finalTotal, true);
      if (this.savingsElement) {
        this.savingsElement.hidden = totals.totalSavings <= 0;
        this.savingsElement.textContent = totals.totalSavings > 0
          ? `Total savings ${this.formatMoney(totals.totalSavings)}`
          : '';
      }

      this.renderAvailableOffers(cart);

      const discountCodes = this.collectDiscountCodes(cart);
      if (this.discountCodeCardElement) {
        this.discountCodeCardElement.hidden = discountCodes.length === 0;
      }
      this.renderDiscountCodeChips(this.discountCodesElement, discountCodes);

      const applications = this.collectDiscountApplications(cart);
      if (this.breakdownCaptionElement) {
        const offerCount = applications.length;
        const codeCount = discountCodes.length;
        this.breakdownCaptionElement.textContent = codeCount
          ? `${codeCount} discount code${codeCount === 1 ? '' : 's'} · tap to view calculation`
          : offerCount
            ? `${offerCount} applied offer${offerCount === 1 ? '' : 's'} · tap to view calculation`
            : totals.totalSavings > 0
              ? `${totals.discountPercent}% saved · tap to view calculation`
              : 'MRP, discounts and final total';
      }
      if (!this.breakdownElement) return;

      const fragment = document.createDocumentFragment();
      fragment.appendChild(this.createBreakdownRow('MRP total', totals.mrpTotal));
      if (totals.productSavings > 0) {
        fragment.appendChild(this.createBreakdownRow('Product price savings', totals.productSavings, 'saving', '−'));
      }
      fragment.appendChild(this.createBreakdownRow('Selling-price subtotal', totals.sellingSubtotal));
      if (totals.itemDiscounts > 0) {
        fragment.appendChild(this.createBreakdownRow('Item-level offers', totals.itemDiscounts, 'saving', '−'));
      }
      if (totals.cartDiscounts > 0) {
        fragment.appendChild(this.createBreakdownRow('Cart-level offers', totals.cartDiscounts, 'saving', '−'));
      }
      if (totals.giftWrapTotal > 0) {
        fragment.appendChild(this.createBreakdownRow('Gift wrap included', totals.giftWrapTotal, 'info'));
      }

      const divider = document.createElement('div');
      divider.className = 'kb-cart__breakdown-divider';
      fragment.appendChild(divider);
      fragment.appendChild(this.createBreakdownRow('Estimated total', totals.finalTotal, 'total'));

      if (applications.length) {
        const offers = document.createElement('div');
        offers.className = 'kb-cart__offer-list';
        const heading = document.createElement('p');
        heading.className = 'kb-cart__offer-heading';
        heading.textContent = 'Applied discounts and offers';
        offers.appendChild(heading);
        applications.forEach((application) => {
          const offer = document.createElement('div');
          offer.className = `kb-cart__offer${application.scope === 'Discount code' ? ' kb-cart__offer--code' : ''}`;
          const copy = document.createElement('span');
          const scope = document.createElement('small');
          scope.textContent = application.scope;
          const title = document.createElement('strong');
          title.textContent = application.title;
          copy.append(scope, title);
          const amount = document.createElement('b');
          amount.textContent = application.amount > 0 ? `−${this.formatMoney(application.amount)}` : 'APPLIED';
          offer.append(copy, amount);
          offers.appendChild(offer);
        });
        fragment.appendChild(offers);
      }

      this.breakdownElement.replaceChildren(fragment);
    }

    createGiftQuantityControl(item, cart = this.cart, native = false) {
      const maximum = Math.max(1, Number(item.quantity || 1));
      const selected = Math.min(maximum, this.getGiftWrapQuantityForItem(item, cart));
      const wrap = document.createElement('div');
      wrap.className = `kb-cart__gift-quantity-picker${native ? ' kb-cart__gift-quantity-picker--native' : ''}`;

      const icon = document.createElement('span');
      icon.className = 'kb-cart__gift-picker-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '🎁';

      const copy = document.createElement('span');
      copy.className = 'kb-cart__gift-copy';
      const title = document.createElement('strong');
      title.textContent = 'Gift wrap quantity';
      const hint = document.createElement('small');
      hint.textContent = maximum > 1
        ? `Choose how many of these ${maximum} items should be gift wrapped`
        : 'Choose whether this item should be gift wrapped';
      copy.append(title, hint);

      const selectWrap = document.createElement('span');
      selectWrap.className = 'kb-cart__gift-select-wrap';
      const select = document.createElement('select');
      select.className = 'kb-cart__gift-select';
      select.dataset.kbGiftQuantity = '';
      select.dataset.key = item.key;
      select.setAttribute('aria-label', `Gift wrap quantity for ${item.product_title || item.title || 'this item'}`);
      for (let quantity = 0; quantity <= maximum; quantity += 1) {
        const option = document.createElement('option');
        option.value = String(quantity);
        option.textContent = quantity === 0
          ? 'No gift wrap'
          : maximum === 1
            ? 'Gift wrap this item'
            : `Gift wrap ${quantity} of ${maximum}`;
        option.selected = quantity === selected;
        select.appendChild(option);
      }
      if (native) {
        select.addEventListener('change', async () => {
          const nextQuantity = Number.parseInt(select.value, 10) || 0;
          if (nextQuantity === 0 && selected > 0) {
            const confirmed = await confirmCartRemoval({
              itemTitle: 'gift wrapping',
              title: 'Remove gift wrapping?',
              message: 'Are you sure you want to remove gift wrapping from this item?',
            });
            if (!confirmed) {
              select.value = String(selected);
              return;
            }
          }
          this.updateGiftQuantity(item.key, nextQuantity, select);
        });
      }
      selectWrap.appendChild(select);
      wrap.append(icon, copy, selectWrap);
      return wrap;
    }

    createItemDiscounts(item) {
      const discounts = this.getItemDiscounts(item);
      if (!discounts.length) return null;
      const list = document.createElement('div');
      list.className = 'kb-cart__item-offers';
      discounts.forEach((discount) => {
        const offer = document.createElement('span');
        offer.className = 'kb-cart__item-offer';
        const title = document.createElement('span');
        title.textContent = discount.title;
        const amount = document.createElement('strong');
        amount.textContent = `−${this.formatMoney(discount.amount)}`;
        offer.append(title, amount);
        list.appendChild(offer);
      });
      return list;
    }

    render(cart) {
      const isEmpty = !cart.items || cart.items.length === 0;
      const totals = this.calculateCartTotals(cart);
      this.classList.toggle('is-empty', isEmpty);
      this.emptyElement.hidden = !isEmpty;
      this.itemsElement.hidden = isEmpty;
      this.footerElement.hidden = isEmpty;
      this.countElement.textContent = String(cart.item_count || 0);
      this.syncCartBubbles(cart.item_count || 0);
      this.renderCartBreakdown(cart, totals);
      this.enhanceNativeCartPage(cart, totals);
      this.enhanceNativeCartFooter(cart, totals);

      if (isEmpty) {
        this.itemsElement.replaceChildren();
        return;
      }

      const fragment = document.createDocumentFragment();
      cart.items.forEach((item, index) => fragment.appendChild(this.createItem(item, index + 1)));
      this.itemsElement.replaceChildren(fragment);
    }

    enhanceNativeCartPage(cart) {
      const nativeCart = document.querySelector('cart-items');
      if (!nativeCart || !Array.isArray(cart?.items)) return;

      cart.items.forEach((item, index) => {
        const line = index + 1;
        const row = nativeCart.querySelector(`#CartItem-${line}`);
        const details = row?.querySelector('.cart-item__details');
        if (!details) return;

        details
          .querySelectorAll('.kb-native-cart-pricing, .kb-native-cart-discounts, .kb-cart__gift-quantity-picker--native')
          .forEach((element) => element.remove());
        details.querySelectorAll(':scope > .caption-with-letter-spacing').forEach((vendor) => vendor.remove());
        details.querySelectorAll('.product-option').forEach((property) => {
          const term = property.querySelector('dt')?.textContent?.replace(/\s+/g, ' ').trim().replace(/:$/, '');
          if (term === GIFT_PROPERTY) property.remove();
        });

        const compareAtPrice = Number(item.kb_compare_at_price || 0);
        const originalPrice = Number(item.original_price ?? item.price ?? 0);
        const finalPrice = Number(item.final_price ?? item.price ?? 0);
        if (compareAtPrice > originalPrice || originalPrice > finalPrice) {
          const pricing = document.createElement('div');
          pricing.className = 'kb-native-cart-pricing';

          const compare = document.createElement('span');
          compare.className = 'kb-native-cart-pricing__mrp';
          compare.textContent = compareAtPrice > originalPrice
            ? `MRP ${this.formatMoney(compareAtPrice)}`
            : `Was ${this.formatMoney(originalPrice)}`;

          const discountBase = compareAtPrice > originalPrice ? compareAtPrice : originalPrice;
          const discountTarget = compareAtPrice > originalPrice ? originalPrice : finalPrice;
          const discount = Math.max(1, Math.round(((discountBase - discountTarget) / discountBase) * 100));
          const badge = document.createElement('span');
          badge.className = 'kb-native-cart-pricing__discount';
          badge.textContent = compareAtPrice > originalPrice ? `${discount}% OFF ON MRP` : `EXTRA ${discount}% OFF`;
          pricing.append(compare, badge);

          const name = details.querySelector('.cart-item__name');
          if (name) name.insertAdjacentElement('afterend', pricing);
          else details.prepend(pricing);
        }

        const discountList = this.createItemDiscounts(item);
        if (discountList) {
          discountList.classList.add('kb-native-cart-discounts');
          details.appendChild(discountList);
        }

        if (this.isGiftWrapItem(item)) return;
        details.appendChild(this.createGiftQuantityControl(item, cart, true));
      });
    }

    enhanceNativeCartFooter(cart, totals) {
      const nativeBlocks = document.querySelector('#main-cart-footer .cart__blocks, .cart__footer .cart__blocks');
      if (!nativeBlocks) return;
      nativeBlocks.classList.add('kb-cart-enhanced');

      const previousAccordion = nativeBlocks.querySelector('.kb-native-cart-breakdown');
      const wasOpen = previousAccordion?.classList.contains('is-open') || false;
      previousAccordion?.remove();
      nativeBlocks.querySelector('.kb-native-discount-code-card')?.remove();

      const discountCodes = this.collectDiscountCodes(cart);
      const codeCard = this.createDiscountCodeCard(discountCodes, true);

      const accordion = document.createElement('section');
      accordion.className = `kb-native-cart-breakdown${wasOpen ? ' is-open' : ''}`;
      const panelId = 'KbNativeCartPriceBreakdown';

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'kb-cart__breakdown-toggle';
      toggle.setAttribute('data-kb-breakdown-toggle', '');
      toggle.setAttribute('aria-expanded', wasOpen ? 'true' : 'false');
      toggle.setAttribute('aria-controls', panelId);

      const icon = document.createElement('span');
      icon.className = 'kb-cart__breakdown-toggle-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '₹';
      const copy = document.createElement('span');
      copy.className = 'kb-cart__breakdown-toggle-copy';
      const title = document.createElement('strong');
      title.textContent = 'Complete price calculation';
      const caption = document.createElement('small');
      const applications = this.collectDiscountApplications(cart);
      caption.textContent = discountCodes.length
        ? `${discountCodes.length} discount code${discountCodes.length === 1 ? '' : 's'} · tap to expand`
        : applications.length
          ? `${applications.length} applied offer${applications.length === 1 ? '' : 's'} · tap to expand`
          : 'MRP, savings, discounts and final total';
      copy.append(title, caption);
      const chevron = document.createElement('span');
      chevron.className = 'kb-cart__breakdown-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      toggle.append(icon, copy, chevron);

      const panel = document.createElement('div');
      panel.id = panelId;
      panel.className = 'kb-cart__breakdown-panel';
      panel.setAttribute('data-kb-breakdown-panel', '');
      panel.hidden = !wasOpen;
      const content = document.createElement('div');
      content.className = 'kb-cart__breakdown-content';
      content.appendChild(this.createBreakdownRow('MRP total', totals.mrpTotal));
      if (totals.productSavings > 0) {
        content.appendChild(this.createBreakdownRow('Product price savings', totals.productSavings, 'saving', '−'));
      }
      content.appendChild(this.createBreakdownRow('Selling-price subtotal', totals.sellingSubtotal));
      if (totals.itemDiscounts > 0) {
        content.appendChild(this.createBreakdownRow('Item-level offers', totals.itemDiscounts, 'saving', '−'));
      }
      if (totals.cartDiscounts > 0) {
        content.appendChild(this.createBreakdownRow('Cart-level offers', totals.cartDiscounts, 'saving', '−'));
      }
      if (totals.giftWrapTotal > 0) {
        content.appendChild(this.createBreakdownRow('Gift wrap included', totals.giftWrapTotal, 'info'));
      }
      const divider = document.createElement('div');
      divider.className = 'kb-cart__breakdown-divider';
      content.appendChild(divider);
      content.appendChild(this.createBreakdownRow('Estimated total', totals.finalTotal, 'total'));

      if (applications.length) {
        const offers = document.createElement('div');
        offers.className = 'kb-cart__offer-list';
        const heading = document.createElement('p');
        heading.className = 'kb-cart__offer-heading';
        heading.textContent = 'Applied discounts and offers';
        offers.appendChild(heading);
        applications.forEach((application) => {
          const offer = document.createElement('div');
          offer.className = `kb-cart__offer${application.scope === 'Discount code' ? ' kb-cart__offer--code' : ''}`;
          const offerCopy = document.createElement('span');
          const scope = document.createElement('small');
          scope.textContent = application.scope;
          const offerTitle = document.createElement('strong');
          offerTitle.textContent = application.title;
          offerCopy.append(scope, offerTitle);
          const amount = document.createElement('b');
          amount.textContent = application.amount > 0 ? `−${this.formatMoney(application.amount)}` : 'APPLIED';
          offer.append(offerCopy, amount);
          offers.appendChild(offer);
        });
        content.appendChild(offers);
      }

      panel.appendChild(content);
      accordion.append(toggle, panel);
      toggle.addEventListener('click', () => this.toggleBreakdown(toggle));

      const ctas = nativeBlocks.querySelector('.cart__ctas');
      const insertionPoint = ctas || nativeBlocks.firstChild;
      if (codeCard) nativeBlocks.insertBefore(codeCard, insertionPoint);
      nativeBlocks.insertBefore(accordion, insertionPoint);
    }

    createItem(item, line) {
      const article = document.createElement('article');
      article.className = 'kb-cart__item';
      article.dataset.key = item.key;
      const isLinkedGiftWrap = Boolean(item.properties?.[GIFT_WRAP_FOR_PROPERTY]);
      if (isLinkedGiftWrap) article.classList.add('kb-cart__item--gift-wrap');

      const imageLink = document.createElement('a');
      imageLink.className = 'kb-cart__image-link';
      imageLink.href = item.url || '#';
      imageLink.tabIndex = item.url ? 0 : -1;

      if (item.image) {
        const image = document.createElement('img');
        image.className = 'kb-cart__image';
        image.src = this.resizeImage(item.image, '240x');
        image.alt = item.product_title || item.title || '';
        image.loading = 'lazy';
        image.width = 120;
        image.height = 120;
        imageLink.appendChild(image);
      }

      const main = document.createElement('div');
      main.className = 'kb-cart__item-main';

      const title = document.createElement('a');
      title.className = 'kb-cart__item-title';
      title.href = item.url || '#';
      title.textContent = item.product_title || item.title || 'Product';
      main.appendChild(title);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variant = document.createElement('p');
        variant.className = 'kb-cart__variant';
        variant.textContent = item.variant_title;
        main.appendChild(variant);
      }

      const propertyEntries = Object.entries(item.properties || {}).filter(
        ([key, value]) => key && key.charAt(0) !== '_' && key !== GIFT_PROPERTY && value
      );
      if (propertyEntries.length) {
        const properties = document.createElement('p');
        properties.className = 'kb-cart__properties';
        properties.textContent = propertyEntries.map(([key, value]) => `${key}: ${value}`).join(' · ');
        main.appendChild(properties);
      }

      const priceRow = document.createElement('div');
      priceRow.className = 'kb-cart__price-row';
      const quantity = Math.max(1, Number(item.quantity || 1));
      const finalLinePrice = Number(item.final_line_price ?? item.line_price ?? 0);
      const originalLinePrice = Number(item.original_line_price ?? item.line_price ?? finalLinePrice);
      const compareAtLinePrice = Number(item.kb_compare_at_price || 0) * quantity;

      const price = document.createElement('span');
      price.className = 'kb-cart__price';
      price.textContent = this.formatMoney(finalLinePrice);
      priceRow.appendChild(price);

      if (compareAtLinePrice > originalLinePrice) {
        const compare = document.createElement('span');
        compare.className = 'kb-cart__compare';
        compare.textContent = `MRP ${this.formatMoney(compareAtLinePrice)}`;
        priceRow.appendChild(compare);

        const discount = Math.max(1, Math.round(((compareAtLinePrice - originalLinePrice) / compareAtLinePrice) * 100));
        const discountBadge = document.createElement('span');
        discountBadge.className = 'kb-cart__discount';
        discountBadge.textContent = `${discount}% OFF ON MRP`;
        priceRow.appendChild(discountBadge);
      } else if (originalLinePrice > finalLinePrice) {
        const compare = document.createElement('span');
        compare.className = 'kb-cart__compare';
        compare.textContent = `Was ${this.formatMoney(originalLinePrice)}`;
        priceRow.appendChild(compare);

        const discount = Math.max(1, Math.round(((originalLinePrice - finalLinePrice) / originalLinePrice) * 100));
        const discountBadge = document.createElement('span');
        discountBadge.className = 'kb-cart__discount kb-cart__discount--extra';
        discountBadge.textContent = `EXTRA ${discount}% OFF`;
        priceRow.appendChild(discountBadge);
      }
      main.appendChild(priceRow);

      const itemDiscounts = this.createItemDiscounts(item);
      if (itemDiscounts) main.appendChild(itemDiscounts);

      if (!this.isGiftWrapItem(item)) {
        main.appendChild(this.createGiftQuantityControl(item));
      } else if (isLinkedGiftWrap) {
        const linkedBadge = document.createElement('p');
        linkedBadge.className = 'kb-cart__gift-linked';
        linkedBadge.textContent = 'Gift wrapping service linked to the selected item';
        main.appendChild(linkedBadge);
      }

      const bottom = document.createElement('div');
      bottom.className = 'kb-cart__item-bottom';

      if (isLinkedGiftWrap) {
        const linkedQuantity = document.createElement('span');
        linkedQuantity.className = 'kb-cart__gift-quantity';
        linkedQuantity.textContent = `Gift wrap quantity: ${item.quantity}`;

        const remove = document.createElement('button');
        remove.className = 'kb-cart__remove';
        remove.type = 'button';
        remove.dataset.kbRemoveGiftToken = String(item.properties?.[GIFT_WRAP_FOR_PROPERTY] || '');
        remove.setAttribute('aria-label', 'Remove gift wrap');
        remove.textContent = 'Remove gift wrap';
        bottom.append(linkedQuantity, remove);
      } else {
        const quantity = document.createElement('div');
        quantity.className = 'kb-cart__quantity';
        quantity.setAttribute('aria-label', `Quantity for ${item.product_title || item.title || 'item'}`);

        const decrease = this.createQuantityButton('decrease', '−', line, item.quantity);
        const input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.value = String(item.quantity);
        input.dataset.kbQuantityInput = '';
        input.dataset.line = String(line);
        input.setAttribute('aria-label', 'Quantity');
        const increase = this.createQuantityButton('increase', '+', line, item.quantity);
        quantity.append(decrease, input, increase);

        const remove = document.createElement('button');
        remove.className = 'kb-cart__remove';
        remove.type = 'button';
        remove.dataset.kbRemoveLine = String(line);
        remove.setAttribute('aria-label', `Remove ${item.product_title || item.title || 'item'}`);
        remove.textContent = 'Remove';
        bottom.append(quantity, remove);
      }
      main.appendChild(bottom);
      article.append(imageLink, main);
      return article;
    }

    createQuantityButton(action, symbol, line, quantity) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.kbQuantityAction = action;
      button.dataset.line = String(line);
      button.dataset.quantity = String(quantity);
      button.setAttribute('aria-label', action === 'increase' ? 'Increase quantity' : 'Decrease quantity');
      button.textContent = symbol;
      return button;
    }

    setLoading(loading) {
      if (!this.loadingElement) return;
      this.loadingElement.hidden = !loading;
      this.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    announce(message) {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      window.setTimeout(() => {
        this.statusElement.textContent = message;
      }, 20);
    }

    syncCartBubbles(count) {
      document.querySelectorAll('#cart-icon-bubble').forEach((cartLink) => {
        let bubble = cartLink.querySelector('.cart-count-bubble');
        if (count <= 0) {
          bubble?.remove();
          return;
        }

        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          cartLink.appendChild(bubble);
        }

        let visibleCount = bubble.querySelector('[aria-hidden="true"]');
        if (!visibleCount) {
          visibleCount = document.createElement('span');
          visibleCount.setAttribute('aria-hidden', 'true');
          bubble.prepend(visibleCount);
        }
        visibleCount.textContent = count < 100 ? String(count) : '';

        let hiddenCount = bubble.querySelector('.visually-hidden');
        if (!hiddenCount) {
          hiddenCount = document.createElement('span');
          hiddenCount.className = 'visually-hidden';
          bubble.appendChild(hiddenCount);
        }
        hiddenCount.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
      });

      document.querySelectorAll('.pmh__cart').forEach((cartLink) => {
        let countBadge = cartLink.querySelector('.pmh__cart-count');
        if (count <= 0) {
          countBadge?.remove();
          return;
        }
        if (!countBadge) {
          countBadge = document.createElement('span');
          countBadge.className = 'pmh__cart-count';
          cartLink.appendChild(countBadge);
        }
        countBadge.textContent = count < 100 ? String(count) : '99+';
      });
    }

    formatMoney(cents, includeCurrency = false) {
      const value = Number(cents || 0) / 100;
      const format =
        (includeCurrency ? this.config.moneyWithCurrencyFormat : this.config.moneyFormat) ||
        this.config.moneyFormat ||
        '${{amount}}';
      const tokenMatch = format.match(/\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?)\s*\}\}/);
      const token = tokenMatch?.[1] || 'amount';
      let formatted;

      switch (token) {
        case 'amount_no_decimals':
          formatted = this.numberWithDelimiters(value, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          formatted = this.numberWithDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = this.numberWithDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = this.numberWithDelimiters(value, 2, "'", '.');
          break;
        default:
          formatted = this.numberWithDelimiters(value, 2, ',', '.');
      }

      return format.replace(tokenMatch?.[0] || '{{amount}}', formatted);
    }

    numberWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (number == null || Number.isNaN(Number(number))) return '0';
      const parts = Number(number).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }

    resizeImage(url, size) {
      if (!url || !size) return url;
      return url.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, `_${size}$1$2`);
    }
  }

  if (!customElements.get('kb-custom-cart')) {
    customElements.define('kb-custom-cart', KBCustomCart);
  }

  const getCart = () => document.querySelector('kb-custom-cart');

  const markGoKwikFlow = (event) => {
    const target = event.target?.closest?.(
      '.gokwik-checkout, #gokwik-buy-now, [class*="gokwik-multi-btn"], [onclick*="GoKwik"], [onclick*="onBuyNowClick"], [onclick*="onCheckoutClick"]'
    );
    if (target) goKwikFlowUntil = Date.now() + 8000;
  };

  document.addEventListener('click', markGoKwikFlow, true);

  document.addEventListener('click', async (event) => {
    const nativeRemove = event.target?.closest?.('cart-remove-button');
    if (nativeRemove && !nativeRemove.closest('kb-custom-cart')) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const row = nativeRemove.closest('.cart-item');
      const itemTitle = row?.querySelector('.cart-item__name')?.textContent;
      const confirmed = await confirmCartRemoval({ itemTitle });
      if (!confirmed) return;

      const cartItems = nativeRemove.closest('cart-items') || nativeRemove.closest('cart-drawer-items');
      if (cartItems?.updateQuantity) {
        cartItems.updateQuantity(nativeRemove.dataset.index, 0, { currentTarget: nativeRemove });
      }
      return;
    }

    const nativeMinus = event.target?.closest?.(
      'cart-items .quantity__button[name="minus"], cart-drawer-items .quantity__button[name="minus"]'
    );
    if (!nativeMinus) return;

    const quantityInput = nativeMinus.closest('quantity-input')?.querySelector('.quantity__input');
    if (Number(quantityInput?.value || 0) > 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const row = nativeMinus.closest('.cart-item');
    const itemTitle = row?.querySelector('.cart-item__name')?.textContent;
    const confirmed = await confirmCartRemoval({ itemTitle });
    if (!confirmed) return;

    const cartItems = nativeMinus.closest('cart-items') || nativeMinus.closest('cart-drawer-items');
    const index = quantityInput?.dataset.index || row?.id?.match(/(\d+)$/)?.[1];
    if (cartItems?.updateQuantity && index) {
      cartItems.updateQuantity(
        index,
        0,
        { currentTarget: row?.querySelector('cart-remove-button') || nativeMinus },
        'minus',
        quantityInput?.dataset.quantityVariantId
      );
    }
  }, true);

  document.addEventListener('change', async (event) => {
    const input = event.target?.closest?.(
      'cart-items .quantity__input, cart-drawer-items .quantity__input'
    );
    if (!input || Number(input.value || 0) > 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const row = input.closest('.cart-item');
    const itemTitle = row?.querySelector('.cart-item__name')?.textContent;
    const confirmed = await confirmCartRemoval({ itemTitle });
    if (!confirmed) {
      input.value = input.getAttribute('value') || '1';
      return;
    }

    const cartItems = input.closest('cart-items') || input.closest('cart-drawer-items');
    if (cartItems?.updateQuantity && input.dataset.index) {
      cartItems.updateQuantity(
        input.dataset.index,
        0,
        { currentTarget: row?.querySelector('cart-remove-button') || input },
        'change',
        input.dataset.quantityVariantId
      );
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (isModifiedClick(event)) return;
    const link = event.target?.closest?.('a[href]');
    if (!link || link.hasAttribute('download') || link.target === '_blank' || link.hasAttribute('data-kb-view-cart')) return;

    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }

    if (url.origin !== window.location.origin || !CART_PAGE_RE.test(url.pathname) || url.searchParams.has('checkout')) return;

    const cart = getCart();
    if (!cart) return;
    event.preventDefault();
    event.stopPropagation();
    cart.setActiveElement(link);
    cart.requestRefresh({ open: true, toast: false, announce: false });
  }, true);

  document.addEventListener('submit', async (event) => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    let action;
    try {
      action = new URL(form.action, window.location.origin);
    } catch (error) {
      return;
    }

    if (action.origin !== window.location.origin || !/\/cart\/add(?:\.js)?\/?$/i.test(action.pathname)) return;

    event.preventDefault();
    const submitter = event.submitter;
    submitter?.setAttribute('aria-disabled', 'true');
    submitter?.classList.add('loading');

    try {
      const response = await fetch(action.pathname.endsWith('.js') ? action.href : `${action.origin}${action.pathname}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          [MANAGED_HEADER]: '1',
        },
        body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item.');
      }
      const cart = getCart();
      cart?.vibrateOnSuccessfulAdd(form);
      cart?.requestRefresh({ open: true, toast: true, announce: true });
    } catch (error) {
      console.error(error);
      getCart()?.announce(error.message || 'Unable to add this item.');
    } finally {
      submitter?.removeAttribute('aria-disabled');
      submitter?.classList.remove('loading');
    }
  });

  const refreshExistingCart = (announce = false) => {
    const cart = getCart();
    if (!cart) return;
    cart.requestRefresh({
      open: cart.classList.contains('is-active'),
      toast: false,
      announce,
    });
  };

  document.addEventListener('cart:refresh', () => refreshExistingCart(false));
  document.addEventListener('cart:updated', () => refreshExistingCart(false));
  document.addEventListener('cart:change', () => refreshExistingCart(false));
  window.addEventListener('pageshow', () =>
    getCart()?.requestRefresh({ open: false, toast: false, announce: false })
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      const [input, init = {}] = args;
      const request = typeof Request !== 'undefined' && input instanceof Request ? input : null;
      const url = new URL(request?.url || input, window.location.origin);
      const method = String(init.method || request?.method || 'GET').toUpperCase();
      const headers = new Headers(init.headers || request?.headers || undefined);
      const isInternal = headers.get(INTERNAL_HEADER) === '1' || headers.get(MANAGED_HEADER) === '1';
      const isMutation = method !== 'GET' && CART_MUTATION_RE.test(url.pathname);
      const isGoKwikFlow = Date.now() < goKwikFlowUntil;

      if (response.ok && isMutation && !isInternal && !isGoKwikFlow) {
        const isAdd = /\/cart\/add/i.test(url.pathname);
        const cart = getCart();
        const shouldOpen = isAdd || cart?.classList.contains('is-active');
        if (isAdd) cart?.vibrateOnSuccessfulAdd();
        cart?.requestRefresh({ open: Boolean(shouldOpen), toast: isAdd, announce: isAdd });
      }
    } catch (error) {
      // Cart observation must never affect the original fetch result.
    }

    return response;
  };
})();
